import { computed, Injectable, Signal, signal } from '@angular/core'
import {
  AppointmentModel,
  AppointmentSelectOption,
  EligibilityBenefits,
  EstimateBalance,
  EstimateData,
  EstimateVisit,
  PatientInsurance,
} from '@mmx/shared'
import { Observable, Subject } from 'rxjs'

export type EstimateBalanceLoadingState = 'idle' | 'loading' | 'error' | 'empty'

export interface EstimateBalanceState {
  balance: EstimateBalance | null
  visits: EstimateVisit[]
  estimate: EstimateData | null
  patientId: string | null
  primaryInsurance: PatientInsurance | null
  insuranceSelectionResolved: boolean
  secondaryInsurance: PatientInsurance | null
  primaryInsuranceOptions: PatientInsurance[]
  secondaryInsuranceOptions: PatientInsurance[]
  hasSecondaryInsurance: boolean
  primaryBenefits: EligibilityBenefits | null
  secondaryBenefits: EligibilityBenefits | null
  selectedAppointmentIds: string[]
  needsPricing: boolean
  dirty: boolean
  chargesLoading: boolean
  primaryBenefitsLoading: boolean
  secondaryBenefitsLoading: boolean
  loadingState: EstimateBalanceLoadingState
  error: string | null
  appointments: AppointmentModel[]
  cocMode: boolean
  unscheduled: boolean
  modifying: boolean
}

const INITIAL_STATE: EstimateBalanceState = {
  balance: null,
  visits: [],
  estimate: null,
  patientId: null,
  primaryInsurance: null,
  insuranceSelectionResolved: false,
  secondaryInsurance: null,
  primaryInsuranceOptions: [],
  secondaryInsuranceOptions: [],
  hasSecondaryInsurance: false,
  primaryBenefits: null,
  secondaryBenefits: null,
  selectedAppointmentIds: [],
  needsPricing: false,
  dirty: false,
  chargesLoading: false,
  primaryBenefitsLoading: false,
  secondaryBenefitsLoading: false,
  loadingState: 'idle',
  error: null,
  appointments: [],
  cocMode: false,
  unscheduled: false,
  modifying: false,
}

@Injectable()
export class EstimateBalanceStateService {
  // Signal-only by design — no toObservable/effect here, so the service runs without an
  // injection context. Consumers bridge to observables on their own side at the RxJS boundary.
  private readonly _state = signal<EstimateBalanceState>(INITIAL_STATE)

  // Sync fan-out for "estimate ref changed" so insurance-loader-like consumers recompute
  // without round-tripping through toObservable. Subject kept private behind the getter.
  private readonly estimateChangesSubject = new Subject<EstimateData | null>()
  readonly estimateChanged$: Observable<EstimateData | null> = this.estimateChangesSubject.asObservable()

  readonly balance = computed(() => this._state().balance)
  readonly visits = computed(() => this._state().visits)
  readonly estimate = computed(() => this._state().estimate)
  readonly patientId = computed(() => this._state().patientId)
  readonly primaryInsurance = computed(() => this._state().primaryInsurance)
  readonly insuranceSelectionResolved = computed(() => this._state().insuranceSelectionResolved)
  readonly secondaryInsurance = computed(() => this._state().secondaryInsurance)
  readonly primaryInsuranceOptions = computed(() => this._state().primaryInsuranceOptions)
  readonly secondaryInsuranceOptions = computed(() => this._state().secondaryInsuranceOptions)
  readonly hasSecondaryInsurance = computed(() => this._state().hasSecondaryInsurance)
  readonly primaryBenefits = computed(() => this._state().primaryBenefits)
  readonly secondaryBenefits = computed(() => this._state().secondaryBenefits)
  readonly selectedAppointmentIds = computed(() => this._state().selectedAppointmentIds)
  readonly needsPricing = computed(() => this._state().needsPricing)
  readonly dirty = computed(() => this._state().dirty)
  readonly chargesLoading = computed(() => this._state().chargesLoading)
  readonly primaryBenefitsLoading = computed(() => this._state().primaryBenefitsLoading)
  readonly secondaryBenefitsLoading = computed(() => this._state().secondaryBenefitsLoading)
  readonly loadingState = computed(() => this._state().loadingState)
  readonly error = computed(() => this._state().error)
  readonly appointments = computed(() => this._state().appointments)
  readonly cocMode = computed(() => this._state().cocMode)
  readonly unscheduled = computed(() => this._state().unscheduled)
  readonly modifying = computed(() => this._state().modifying)

  readonly selectedAppointments: Signal<AppointmentModel[]> = computed(() => {
    const byId = new Map(this.appointments().map(a => [a.id, a]))
    return this.selectedAppointmentIds()
      .map(id => byId.get(id))
      .filter((a): a is AppointmentModel => a != null && a.startAt != null)
      .sort((a, b) => Number(a.startAt?.valueOf() ?? 0) - Number(b.startAt?.valueOf() ?? 0))
  })

  readonly appointmentOptions: Signal<AppointmentSelectOption[]> = computed(() => {
    const appointments = this.appointments()
    const selectedIds = this.selectedAppointmentIds()
    const currentEstimateId = this.estimate()?.id
    // Visit numbers are a Course-of-Care sequence over appointments eligible for THIS estimate —
    // one already tied to another estimate isn't one of this estimate's visits, so it's skipped
    // rather than consuming a number that shifts every appointment after it.
    let nextVisitNumber = 1
    return appointments.map(appointment => {
      const hasOtherEstimate = appointment.estimate != null
        && appointment.estimateId != null
        && appointment.estimateId !== currentEstimateId
      return {
        value: appointment.id,
        viewValue: appointment.text,
        disabled: hasOtherEstimate,
        hasEstimate: hasOtherEstimate,
        appointment,
        // Same CPT accessor used by every estimate-balance service — single source of truth.
        hint: (appointment.getCptCodes?.() ?? []).join(', '),
        visitNumber: hasOtherEstimate ? undefined : nextVisitNumber++,
        selected: selectedIds.includes(appointment.id),
      }
    })
  })

  // Atomic loading→idle transition; prefer over ad-hoc setEstimate/setBalance from callers.
  //
  // Intentionally NOT reset (the spread preserves them):
  //   - `appointments` — picker cache survives estimate nav; refreshAppointments() refills.
  //   - `cocMode` / `unscheduled` — UI flags persist on same-estimate reload (resetForNewEstimate clears them across estimates).
  //   - `chargesLoading` — short-lived per-request flag owned by the flow service.
  //   - `primary/secondaryInsurance` + options — EstimateInsuranceLoader drives them off
  //     PatientDataService.insurances$, keyed to patient, so they survive estimate reload on the same patient.
  // When adding a new state slot, decide whether it belongs here OR in resetForNewEstimate — never silently in neither.
  hydrated(estimate: EstimateData, balance: EstimateBalance | null, visits: EstimateVisit[]): void {
    this._state.update(s => ({
      ...s,
      estimate,
      patientId: estimate.patientId ?? null,
      selectedAppointmentIds: [...(estimate.appointmentIds ?? [])],
      balance,
      visits,
      dirty: false,
      needsPricing: false,
      error: null,
      loadingState: 'idle',
      modifying: estimate.modifySnapshot != null,
    }))
    this.estimateChangesSubject.next(estimate)
  }

  setBalance(balance: EstimateBalance | null): void {
    this._state.update(s => ({ ...s, balance }))
  }

  setEstimate(estimate: EstimateData | null): void {
    this._state.update(s => ({ ...s, estimate }))
    this.estimateChangesSubject.next(estimate)
  }

  setPatientId(patientId: string | null): void {
    this._state.update(s => ({ ...s, patientId }))
  }

  setPrimaryInsurance(insurance: PatientInsurance | null): void {
    this._state.update(s => ({ ...s, primaryInsurance: insurance }))
  }

  setSecondaryInsurance(insurance: PatientInsurance | null): void {
    this._state.update(s => ({ ...s, secondaryInsurance: insurance }))
  }

  setPrimaryInsuranceOptions(options: PatientInsurance[]): void {
    this._state.update(s => ({ ...s, primaryInsuranceOptions: options, insuranceSelectionResolved: true }))
  }

  setSecondaryInsuranceOptions(options: PatientInsurance[]): void {
    this._state.update(s => ({ ...s, secondaryInsuranceOptions: options }))
  }

  // Toggling OFF also clears the cached selection — the save payload reads state directly,
  // so a stale secondary would persist on the next save. Atomic so both signals settle in one tick.
  setHasSecondaryInsurance(hasSecondary: boolean): void {
    this._state.update(s => ({
      ...s,
      hasSecondaryInsurance: hasSecondary,
      secondaryInsurance: hasSecondary ? s.secondaryInsurance : null,
    }))
  }

  setPrimaryBenefits(benefits: EligibilityBenefits | null): void {
    this._state.update(s => ({ ...s, primaryBenefits: benefits }))
  }

  setSecondaryBenefits(benefits: EligibilityBenefits | null): void {
    this._state.update(s => ({ ...s, secondaryBenefits: benefits }))
  }

  setSelectedAppointmentIds(ids: string[]): void {
    this._state.update(s => ({ ...s, selectedAppointmentIds: [...ids] }))
  }

  setPrimaryBenefitsLoading(loading: boolean): void {
    this._state.update(s => ({ ...s, primaryBenefitsLoading: loading }))
  }

  setSecondaryBenefitsLoading(loading: boolean): void {
    this._state.update(s => ({ ...s, secondaryBenefitsLoading: loading }))
  }

  setChargesLoading(loading: boolean): void {
    this._state.update(s => ({ ...s, chargesLoading: loading }))
  }

  setAppointments(appointments: AppointmentModel[]): void {
    this._state.update(s => ({ ...s, appointments }))
  }

  setCocMode(on: boolean): void {
    this._state.update(s => ({ ...s, cocMode: on }))
  }

  setUnscheduled(on: boolean): void {
    this._state.update(s => ({ ...s, unscheduled: on }))
  }

  setModifying(on: boolean): void {
    this._state.update(s => ({ ...s, modifying: on }))
  }

  markDirty(): void {
    this._state.update(s => ({ ...s, dirty: true, needsPricing: true }))
  }

  markSaved(): void {
    this._state.update(s => ({ ...s, dirty: false }))
  }

  clearDirty(): void {
    this._state.update(s => ({ ...s, dirty: false, needsPricing: false }))
  }

  startLoading(): void {
    this._state.update(s => ({ ...s, loadingState: 'loading', error: null }))
  }

  markError(message: string): void {
    this._state.update(s => ({ ...s, loadingState: 'error', error: message }))
  }

  resetForNewEstimate(nextPatientId: string | null = null): void {
    // Preserve the same loader-owned/cache slots hydrated() carves out. The coverage cluster
    // (P/S selection, options, hasSecondaryInsurance gate) is owned by EstimateInsuranceLoader off
    // PatientDataService.insurances$ — blanking it here strands the gate, because the loader's
    // (P,S) dedup won't re-resolve an unchanged (null,null) draft to restore it (refresh repro).
    // Do not preserve it across patient changes: save serialises insurance state directly.
    this._state.update(s => ({
      ...INITIAL_STATE,
      appointments: s.appointments,
      ...(this.canPreserveCoverageCluster(s, nextPatientId) ? {
        primaryInsurance: s.primaryInsurance,
        insuranceSelectionResolved: s.insuranceSelectionResolved,
        secondaryInsurance: s.secondaryInsurance,
        primaryInsuranceOptions: s.primaryInsuranceOptions,
        secondaryInsuranceOptions: s.secondaryInsuranceOptions,
        hasSecondaryInsurance: s.hasSecondaryInsurance,
      } : {}),
    }))
  }

  private canPreserveCoverageCluster(s: EstimateBalanceState, nextPatientId: string | null): boolean {
    if (!nextPatientId) {
      return true
    }
    if (s.patientId !== nextPatientId) {
      return false
    }
    return this.insuranceMatchesPatient(s.primaryInsurance, nextPatientId)
      && this.insuranceMatchesPatient(s.secondaryInsurance, nextPatientId)
      && s.primaryInsuranceOptions.every(i => this.insuranceMatchesPatient(i, nextPatientId))
      && s.secondaryInsuranceOptions.every(i => this.insuranceMatchesPatient(i, nextPatientId))
  }

  private insuranceMatchesPatient(insurance: PatientInsurance | null, patientId: string): boolean {
    return !insurance?.patientId || insurance.patientId === patientId
  }
}
